<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MCreateFixedAsset',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    assetType: {
      type: String,
      required: false,
      default: 'general',
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formName = ref('rental');
  const formId = ref(null);
  const item = ref(null);
  const fixedAssetTypes = ref([
    {
      id: 'general',
      name: $fn.value.pgettext('FixedAssetTypes', 'General'),
    },
    {
      id: 'waste_bin',
      name: $fn.value.pgettext('FixedAssetTypes', 'Waste Bin'),
    },
  ]);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    formName: formName.value,
    formId: formId.value,
    item: item.value,
    fixedAssetTypes: fixedAssetTypes.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return $fn.value.schemaDefaults('CreateFixedAsset', {
      asset_type: props.assetType,
      waste_bin: {
        bin_type: null,
        bin_number: null,
        capacity: {
          amount: null,
          unit: 'liter',
        },
      },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formName' },
      { name: 'formId' },
      { name: 'item' },
      { name: 'fixedAssetTypes' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formName,
      formId,
      item,
      fixedAssetTypes,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Create Fixed Asset') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.assetType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('assetType');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ydn44ttrt_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_drmdwf80g_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_navigate_nbt150cbw_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.fixedAsset',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const act_1040167445267876_reloadSlickgrid_c4o9agv86_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'fixedAssets',
    };
  };
  const act_1040167445267876_reloadSlickgrid_rk6nntzdk_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'wasteBins',
    };
  };
  const act_1040167445267876_request_4md1iz5yq_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_fixed_asset',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_ydn44ttrt =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ydn44ttrt_getActionArgs,
    });
  const inst_evh_7315092382398562_close_n1wa17nm4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ydn44ttrt,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_n1wa17nm4],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_nflsp6oi7 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_p0jixf84x =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_nflsp6oi7,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_p0jixf84x],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_1848i8pz9 =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_q5dvdwg31 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_1848i8pz9,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_q5dvdwg31],
    event: 'close',
  });
  const act_1040167445267876_closeModal_drmdwf80g =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_drmdwf80g_getActionArgs,
    });
  const inst_evh_1040167445267876_success_fm2h4eyoe =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_drmdwf80g,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_navigate_nbt150cbw =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_nbt150cbw_getActionArgs,
    });
  const inst_evh_1040167445267876_success_47i4gzfbx =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_nbt150cbw,
      event: 'success',
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadSlickgrid_c4o9agv86 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_c4o9agv86_getActionArgs,
    });
  const inst_evh_1040167445267876_success_z348p1aw1 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_c4o9agv86,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_reloadSlickgrid_rk6nntzdk =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_rk6nntzdk_getActionArgs,
    });
  const inst_evh_1040167445267876_success_d0rtlwv60 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_rk6nntzdk,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_request_4md1iz5yq = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_4md1iz5yq_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_success_fm2h4eyoe,
      inst_evh_1040167445267876_success_47i4gzfbx,
      inst_evh_1040167445267876_success_z348p1aw1,
      inst_evh_1040167445267876_success_d0rtlwv60,
    ],
  });
  const inst_evh_1040167445267876_submit_wqaf0c2dz =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_4md1iz5yq,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_wqaf0c2dz],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_nex93fous =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_qkmk5mdax =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_nex93fous,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_qkmk5mdax],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_n1wa17nm4,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_p0jixf84x,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_q5dvdwg31,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_fm2h4eyoe,
    inst_evh_1040167445267876_success_47i4gzfbx,
    inst_evh_1040167445267876_success_z348p1aw1,
    inst_evh_1040167445267876_success_d0rtlwv60,
    inst_evh_1040167445267876_submit_wqaf0c2dz,
    create: inst_evh_1040167445267876_submit_wqaf0c2dz,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_qkmk5mdax,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_n1wa17nm4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_n1wa17nm4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_p0jixf84x = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_p0jixf84x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_q5dvdwg31 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_q5dvdwg31.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_fm2h4eyoe = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_fm2h4eyoe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_47i4gzfbx = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_47i4gzfbx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_z348p1aw1 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_z348p1aw1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_d0rtlwv60 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_d0rtlwv60.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_wqaf0c2dz = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_wqaf0c2dz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_qkmk5mdax = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_qkmk5mdax.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MCreateFixedAsset"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateFixedAsset"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetFixedAssetGeneral :fixedAsset="item">
        </FieldsetFixedAssetGeneral>
        <hr v-if="assetType === 'waste_bin'" />
        <FieldsetFixedAssetWasteBin
          :fixedAsset="item"
          v-if="assetType === 'waste_bin'"
        >
        </FieldsetFixedAssetWasteBin>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.create && actions.create.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create && actions.create.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
